import { ErrorCode } from './generated/errorCodes.gen';
import { isStaleWrite } from './utils/hasErrorCode';

/** One document the legal wall says the user must accept again. */
export interface LegalStaleDoc {
  docType: string;
  requiredSequence: number;
}

/** A validation ProblemDetails' `errors`: each field's messages. */
export type FieldErrors = Readonly<Record<string, readonly string[]>>;

/** Why a request failed. Only `campaign-changed` is never parsed from a response: the write layer raises it before sending. */
export type FailureReason =
  | {
      kind: 'refused';
      /** The code when the API declares it; `rawCode` keeps whatever the body sent. */
      code: ErrorCode | null;
      rawCode: string | null;
      message: string;
      /** The server's `nextAction`, when the refusal names one. */
      next: string | null;
      retryAfterSeconds: number | null;
      fields: FieldErrors | null;
      /** A typed payload a write declares for its code; null until it does. */
      payload: unknown;
    }
  | { kind: 'stale'; entity: string | null; id: string | null; currentVersion: number | null }
  | { kind: 'signed-out' }
  | { kind: 'payment-required'; subscriptionStatus: string | null }
  | { kind: 'legal-wall'; stale: LegalStaleDoc[] }
  | { kind: 'unreachable' }
  | { kind: 'server'; status: number }
  | { kind: 'campaign-changed' };

const UNREACHABLE_MESSAGE = 'Cannot connect to server. Please check your internet connection.';

const DECLARED_CODES: ReadonlySet<string> = new Set(Object.values(ErrorCode));

/** A 409 body is the legal wall (owes a re-consent), not a conflict, when it carries this code and the stale list. */
export function isLegalWall(
  body: { code?: unknown; stale?: unknown } | null | undefined,
): body is { code: string; stale: LegalStaleDoc[]; message?: string } {
  return body?.code === ErrorCode.legal_acceptance_required && Array.isArray(body.stale);
}

/**
 * The one error an HTTP door throws. `status` is 0 when no response arrived. `code`, `body` and
 * `retryAfterSeconds` stay flat for the callers that read them off the old `ApiError`; `reason` is
 * what a caller branches on.
 */
export class ApiFailure extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly retryAfterSeconds: number | null;
  /** The whole refusal body, for the fields a refusal carries beyond its sentence and code. */
  readonly body: Record<string, unknown>;
  readonly reason: FailureReason;

  constructor(
    status: number,
    code: string | null,
    message: string,
    retryAfterSeconds: number | null = null,
    body: Record<string, unknown> = {},
    reason: FailureReason = reasonOf(status, code, message, retryAfterSeconds, body),
  ) {
    super(message);
    this.name = 'ApiFailure';
    this.status = status;
    this.code = code;
    this.retryAfterSeconds = retryAfterSeconds;
    this.body = body;
    this.reason = reason;
  }
}

/** The failure a non-ok response means. `message` replaces the server's sentence when the caller names its own. */
export function failureFromResponse(
  response: Pick<Response, 'status' | 'headers'>,
  body: unknown,
  message?: string,
): ApiFailure {
  const fields = recordOf(body);
  const code = typeof fields.code === 'string' ? fields.code : null;
  return new ApiFailure(
    response.status,
    code,
    message ?? messageOf(response.status, fields),
    retryAfterOf(response, fields),
    fields,
  );
}

/**
 * The failure a rejected request means: itself when it already is one, unreachable when fetch
 * rejected with a TypeError (no response arrived). Null for anything else, an abort included,
 * which the door rethrows unchanged.
 */
export function failureFromThrown(error: unknown): ApiFailure | null {
  if (error instanceof ApiFailure) return error;
  if (error instanceof TypeError) return new ApiFailure(0, null, UNREACHABLE_MESSAGE);
  return null;
}

function reasonOf(
  status: number,
  code: string | null,
  message: string,
  retryAfterSeconds: number | null,
  body: Record<string, unknown>,
): FailureReason {
  if (status === 0) return { kind: 'unreachable' };
  if (status === 401) return { kind: 'signed-out' };
  if (status === 402) return { kind: 'payment-required', subscriptionStatus: stringOrNull(body.subscriptionStatus) };
  if (isLegalWall(body)) return { kind: 'legal-wall', stale: body.stale };
  if (isStaleWrite({ code })) {
    return {
      kind: 'stale',
      entity: stringOrNull(body.entity),
      id: stringOrNull(body.id),
      currentVersion: typeof body.currentVersion === 'number' ? body.currentVersion : null,
    };
  }
  if (status >= 500 && code === null) return { kind: 'server', status };
  return {
    kind: 'refused',
    code: code !== null && DECLARED_CODES.has(code) ? (code as ErrorCode) : null,
    rawCode: code,
    message,
    next: stringOrNull(body.nextAction),
    retryAfterSeconds,
    fields: fieldErrorsOf(body.errors),
    payload: null,
  };
}

/**
 * The sentence to show: the refusal's `error`, the middleware's `message`, a validation
 * ProblemDetails' first field error, then its title. The legal wall's `error` is its code, so its
 * sentence is `message`.
 */
function messageOf(status: number, body: Record<string, unknown>): string {
  const candidates = isLegalWall(body)
    ? [body.message]
    : [body.error, body.message, Object.values(fieldErrorsOf(body.errors) ?? {})[0]?.[0], body.title];
  const sentence = candidates.find((c): c is string => typeof c === 'string' && c.length > 0);
  return sentence ?? `HTTP ${status}`;
}

function retryAfterOf(response: Pick<Response, 'headers'>, body: Record<string, unknown>): number | null {
  // Some web test doubles hand over a Response without headers.
  const header = response.headers?.get('Retry-After') ?? null;
  if (header !== null && /^\d+$/.test(header.trim())) return Number(header.trim());
  if (header !== null) {
    const at = Date.parse(header);
    if (!Number.isNaN(at)) return Math.max(0, Math.ceil((at - Date.now()) / 1000));
  }
  return typeof body.retryAfterSeconds === 'number' ? body.retryAfterSeconds : null;
}

function fieldErrorsOf(errors: unknown): FieldErrors | null {
  if (typeof errors !== 'object' || errors === null || Array.isArray(errors)) return null;
  const entries = Object.entries(errors).filter(
    (entry): entry is [string, string[]] => Array.isArray(entry[1]) && entry[1].every((m) => typeof m === 'string'),
  );
  return entries.length === 0 ? null : Object.fromEntries(entries);
}

function recordOf(body: unknown): Record<string, unknown> {
  return typeof body === 'object' && body !== null && !Array.isArray(body) ? (body as Record<string, unknown>) : {};
}

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}
